import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import WorkspaceLayout from './workspace-layout';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import { roleSelectItems, WorkspaceRoles, SSOConfig, UpsertSSOConfigRequest } from '@/lib/types';
import api from '@/utils/api';

// group_role_mappings is a Record<string, number> on the wire; admins edit it as
// "group=roleId" lines. These helpers (de)serialize at the form boundary.
const mappingsToText = (m: Record<string, number>) =>
  Object.entries(m || {})
    .map(([group, role]) => `${group}=${role}`)
    .join('\n');

const textToMappings = (text: string): Record<string, number> => {
  const out: Record<string, number> = {};
  text.split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const idx = trimmed.indexOf('=');
    if (idx < 0) return;
    const key = trimmed.slice(0, idx).trim();
    const val = parseInt(trimmed.slice(idx + 1).trim(), 10);
    if (key && !Number.isNaN(val)) out[key] = val;
  });
  return out;
};

const textToDomains = (text: string) =>
  text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

const SSOSettings = () => {
  const { workspaceId } = useParams();
  const isAdmin = useIsInAdminGroup();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');

  const [issuerUrl, setIssuerUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  // Default to the lowest-privilege role: SSO only bootstraps members in as
  // Viewers; admins elevate individuals in Users afterwards.
  const [defaultRoleId, setDefaultRoleId] = useState<number>(WorkspaceRoles.WorkspaceViewer);
  const [domainsText, setDomainsText] = useState('');
  const [mappingsText, setMappingsText] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`workspace/${workspaceId}/sso`);
        const cfg: SSOConfig = res.data?.resource ?? res.data;
        setExists(true);
        setIssuerUrl(cfg.issuer_url);
        setClientId(cfg.client_id);
        setDefaultRoleId(cfg.default_role_id);
        setDomainsText((cfg.allowed_domains || []).join(', '));
        setMappingsText(mappingsToText(cfg.group_role_mappings));
        setEnabled(cfg.enabled);
        setLoginUrl(cfg.login_url);
      } catch (err: any) {
        // 404 simply means SSO has never been configured for this workspace.
        if (err?.response?.status !== 404) {
          toaster.create({ type: 'error', title: 'Failed to load SSO settings' });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [workspaceId, isAdmin]);

  const onSave = async () => {
    if (!issuerUrl.trim() || !clientId.trim()) {
      toaster.create({ type: 'error', title: 'Issuer URL and client ID are required' });
      return;
    }
    if (!exists && !clientSecret.trim()) {
      toaster.create({ type: 'error', title: 'Client secret is required to configure SSO' });
      return;
    }

    const payload: UpsertSSOConfigRequest = {
      issuer_url: issuerUrl.trim(),
      client_id: clientId.trim(),
      default_role_id: defaultRoleId,
      group_role_mappings: textToMappings(mappingsText),
      allowed_domains: textToDomains(domainsText),
      enabled,
    };
    // Only send the secret when the admin actually typed one — blank preserves
    // the stored (encrypted) secret on update.
    if (clientSecret.trim()) {
      payload.client_secret = clientSecret.trim();
    }

    setSaving(true);
    try {
      const res = await api.put(`workspace/${workspaceId}/sso`, payload);
      const cfg: SSOConfig = res.data?.resource ?? res.data;
      setExists(true);
      setLoginUrl(cfg.login_url);
      setClientSecret('');
      toaster.create({ type: 'success', title: 'SSO settings saved' });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Failed to save SSO settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Remove SSO for this workspace? Members will fall back to password login.')) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`workspace/${workspaceId}/sso`);
      setExists(false);
      setIssuerUrl('');
      setClientId('');
      setClientSecret('');
      setDomainsText('');
      setMappingsText('');
      setEnabled(true);
      setLoginUrl('');
      toaster.create({ type: 'success', title: 'SSO configuration removed' });
    } catch {
      toaster.create({ type: 'error', title: 'Failed to remove SSO configuration' });
    } finally {
      setSaving(false);
    }
  };

  const copyLoginUrl = () => {
    navigator.clipboard?.writeText(loginUrl);
    toaster.create({ type: 'info', title: 'Login URL copied' });
  };

  if (!isAdmin) {
    return (
      <WorkspaceLayout>
        <Container maxW='2xl' py={10}>
          <Text>You do not have permission to manage SSO settings.</Text>
        </Container>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <Card.Root mx='auto' width={{ md: '90%' }} maxW='3xl'>
        <Card.Header>
          <Card.Title fontSize='2xl'>Single sign-on (OIDC)</Card.Title>
          <Text color='fg.muted' fontSize='sm'>
            Connect your identity provider. Members signing in through it are provisioned
            automatically — no passwords are stored here.
          </Text>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <Flex justify='center' py={10}>
              <Spinner />
            </Flex>
          ) : (
            <Stack gap={5}>
              <Field
                label='Issuer URL'
                helperText='Your IdP’s OIDC issuer (discovery is read from /.well-known/openid-configuration).'
              >
                <Input
                  value={issuerUrl}
                  onChange={(e) => setIssuerUrl(e.target.value)}
                  placeholder='https://login.acme.com'
                />
              </Field>

              <Field label='Client ID'>
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </Field>

              <Field
                label='Client secret'
                helperText={
                  exists
                    ? 'Stored securely. Leave blank to keep the current secret.'
                    : 'Required. Encrypted at rest.'
                }
              >
                <Input
                  type='password'
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={exists ? '••••••••' : ''}
                />
              </Field>

              <Field
                label='Initial role'
                helperText='Granted on a member’s first SSO login only. Change roles afterward in Users — SSO never overwrites them.'
              >
                <SimpleSelect
                  items={roleSelectItems}
                  value={String(defaultRoleId)}
                  onValueChange={(v) => setDefaultRoleId(Number(v))}
                />
              </Field>

              <Field
                label='Group → role mappings (optional)'
                helperText='One per line, "group=roleId". Sets the INITIAL role for matching members on first login only — leave empty to start everyone at the role above. Role IDs: 3 = Admin, 4 = Contributor, 5 = Viewer.'
              >
                <Textarea
                  value={mappingsText}
                  onChange={(e) => setMappingsText(e.target.value)}
                  placeholder={'admins=3\neditors=4'}
                  rows={3}
                />
              </Field>

              <Field
                label='Allowed email domains'
                helperText='Optional. Comma-separated. Leave empty to allow any domain.'
              >
                <Input
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  placeholder='acme.com, acme.io'
                />
              </Field>

              <Field>
                <Checkbox
                  colorPalette='brand'
                  checked={enabled}
                  onCheckedChange={({ checked }) => setEnabled(!!checked)}
                >
                  Enabled
                </Checkbox>
              </Field>

              {exists && loginUrl && (
                <Field
                  label='SSO login URL'
                  helperText='Generated automatically for this workspace. Share it with members or link it from your app — and register its /callback variant as a redirect URI in your IdP.'
                >
                  <HStack>
                    <Input value={loginUrl} readOnly />
                    <Button variant='outline' onClick={copyLoginUrl}>
                      Copy
                    </Button>
                  </HStack>
                </Field>
              )}

              <Flex justify='space-between' pt={2}>
                <Box>
                  {exists && (
                    <Button variant='danger' onClick={onDelete} disabled={saving}>
                      Remove SSO
                    </Button>
                  )}
                </Box>
                <Button onClick={onSave} loading={saving} loadingText='Saving...'>
                  Save SSO settings
                </Button>
              </Flex>
            </Stack>
          )}
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
};

export default SSOSettings;
